import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, finalize } from 'rxjs';
import { ApiBridgeService } from 'src/app/api-bridge.service';
import { State } from '../pages/profile/pages/personal-data/model/location';

@Injectable({
  providedIn: 'root',
})
export class PlatformService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = [];
  isLoading$: Observable<boolean> | undefined;

  constructor(
    private apiBridgeService: ApiBridgeService,
    private router: Router,
    private httpClient: HttpClient
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }
  isLoadingSubject = new BehaviorSubject<boolean>(false);

  getCBU(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/cbus`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  addCBU(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/cbus`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getBanks(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `utils/banks`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  deleteCBU(id: string): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/cbus/${id}`;

    return this.apiBridgeService
      .delete(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  myAccount(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/account`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  changePassword(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/password`;

    return this.apiBridgeService
      .put(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  myMovements(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/movements`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getPortfolio(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/portfolio`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getOpportunities(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/projects/opportunities`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getPuntas(projectId: any): Observable<any> {
    const endpoint = `app/auth/projects/${projectId}/puntas`;

    return this.apiBridgeService.get(endpoint);
  }

  getMarketProjects(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/projects/marketplace`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getProjectInvest(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/movements/projects`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  withdrawMoney(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/cashout`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  addDocuments(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/documents`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  signDDJJ(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/sign`;

    return this.apiBridgeService
      .put(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getDocuments(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/documents`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  updatePhone(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/phones`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  verifyPhone(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/phones/verify`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  createOrder(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/investments/orders`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  addAddress(body: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/user/address`;

    return this.apiBridgeService
      .post(endpoint, body)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getStates() {
    this.isLoadingSubject.next(true);

    const endpoint = 'utils/provinces';

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getLocality(state: string) {
    this.isLoadingSubject.next(true);
    const endpoint = `https://apis.datos.gob.ar/georef/api/localidades?provincia=${state}`;

    return this.httpClient
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getNotifications() {
    this.isLoadingSubject.next(true);
    const endpoint = `app/auth/notifications`;

    return this.apiBridgeService
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  viewedNotification(notificationId: any): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/notifications/${notificationId}/viewed`;

    return this.apiBridgeService
      .put(endpoint, {notificationId})
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  viewedAllNotification(): Observable<any> {
    this.isLoadingSubject.next(true);

    const endpoint = `app/auth/notifications/all/viewed`;

    return this.apiBridgeService
      .put(endpoint, {})
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
